import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Skeleton } from '../../../components/ui/skeleton';
import { Calendar, DollarSign, Loader2, ArrowRight, Circle } from 'lucide-react';
import { CLOSED_STAGES } from '../pipeline/constants';

export const PipelineBoard = ({ deals, formatCurrency, formatDate, getDealsForStage, handleMoveNext, loading, pipelineInfo, pipelineStages, setMoveDialogOpen, setSelectedDeal, updating }) => (
      <Card className="overflow-hidden">
        <CardHeader className="pb-2 px-3 sm:px-4 md:px-6">
          <CardTitle className="text-base sm:text-lg">Pipeline Board</CardTitle>
          <CardDescription className="text-xs sm:text-sm">Swipe horizontally to view all stages</CardDescription>
        </CardHeader>
        <CardContent className="p-0 sm:p-2 md:p-4">
          {/* Horizontal scroll container with touch support */}
          <div className="overflow-x-auto overflow-y-hidden scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
            <div className="flex gap-2 sm:gap-3 p-2 sm:p-0 pb-4 min-w-max">
              {pipelineStages.map((stage, index) => {
                const stageDeals = getDealsForStage(stage.id);
                const StageIcon = stage.icon;
                const isLastStage = index === pipelineStages.length - 1;
                
                return (
                  <div 
                    key={stage.id} 
                    className="w-56 sm:w-64 md:w-72 flex-shrink-0"
                    data-testid={`stage-column-${stage.id}`}
                  >
                    {/* Stage Header */}
                    <div className={`rounded-t-lg px-2 sm:px-3 py-1.5 sm:py-2 ${stage.color} text-white flex items-center justify-between`}>
                      <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
                        <StageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4 flex-shrink-0" />
                        <span className="font-medium text-xs sm:text-sm truncate" title={stage.label}>
                          {stage.label}
                        </span>
                      </div>
                      <Badge variant="secondary" className="bg-white/20 text-white text-xs flex-shrink-0 ml-1">
                        {stageDeals.length}
                      </Badge>
                    </div>
                    
                    {/* Stage Content - Scrollable within column */}
                    <div className={`min-h-[250px] sm:min-h-[300px] md:min-h-[400px] max-h-[60vh] overflow-y-auto rounded-b-lg border-2 border-t-0 ${stage.lightColor} p-1.5 sm:p-2 space-y-1.5 sm:space-y-2`}>
                      {loading ? (
                        <div className="space-y-2">
                          <Skeleton className="h-20 sm:h-24 w-full" />
                          <Skeleton className="h-20 sm:h-24 w-full" />
                        </div>
                      ) : stageDeals.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-24 sm:h-32 text-gray-400">
                          <Circle className="w-6 h-6 sm:w-8 sm:h-8 mb-1 sm:mb-2 opacity-50" />
                          <span className="text-xs sm:text-sm">No deals</span>
                        </div>
                      ) : (
                        stageDeals.map(deal => (
                          <Card 
                            key={deal.bigin_id || deal.id} 
                            className="cursor-pointer hover:shadow-md transition-shadow bg-white dark:bg-gray-800"
                            data-testid="deal-card"
                          >
                            <CardContent className="p-2 sm:p-3">
                              <div className="flex items-start justify-between mb-1.5 sm:mb-2">
                                <h4 className="font-medium text-xs sm:text-sm text-gray-900 dark:text-white truncate max-w-[140px] sm:max-w-[180px]" title={deal.Deal_Name}>
                                  {deal.Deal_Name}
                                </h4>
                                {updating === (deal.bigin_id || deal.id) && (
                                  <Loader2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin text-blue-500 flex-shrink-0" />
                                )}
                              </div>
                              
                              <div className="space-y-0.5 sm:space-y-1 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <DollarSign className="w-3 h-3 flex-shrink-0" />
                                  <span className="truncate">{formatCurrency(deal.Amount)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 flex-shrink-0" />
                                  <span>{formatDate(deal.Closing_Date)}</span>
                                </div>
                              </div>
                              
                              {/* Action Buttons */}
                              <div className="flex gap-1 mt-1.5 sm:mt-2 pt-1.5 sm:pt-2 border-t">
                                {!isLastStage && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="flex-1 h-6 sm:h-7 text-xs px-1 sm:px-2"
                                    onClick={() => handleMoveNext(deal)}
                                    disabled={updating === (deal.bigin_id || deal.id)}
                                    data-testid="move-next-stage-button"
                                  >
                                    <ArrowRight className="w-3 h-3 mr-0.5 sm:mr-1" />
                                    <span className="hidden sm:inline">Next</span>
                                    <span className="sm:hidden">→</span>
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="flex-1 h-6 sm:h-7 text-xs px-1 sm:px-2"
                                  onClick={() => {
                                    setSelectedDeal(deal);
                                    setMoveDialogOpen(true);
                                  }}
                                  disabled={updating === (deal.bigin_id || deal.id)}
                                  data-testid="move-to-stage-button"
                                >
                                  <span className="hidden sm:inline">Move to...</span>
                                  <span className="sm:hidden">Move</span>
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))
                      )}
                    </div>
                  </div>
                );
              })}
              
              {/* Closed Stages */}
              {CLOSED_STAGES.filter(cs => 
                pipelineInfo?.stages?.some(s => s.name === cs.id)
              ).map(stage => {
                const stageDeals = getDealsForStage(stage.id);
                const StageIcon = stage.icon;
                
                return (
                  <div key={stage.id} className="w-44 sm:w-48 md:w-56 flex-shrink-0">
                    <div className={`rounded-t-lg px-2 sm:px-3 py-1.5 sm:py-2 ${stage.color} text-white flex items-center justify-between`}>
                      <div className="flex items-center gap-1.5 sm:gap-2">
                        <StageIcon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="font-medium text-xs sm:text-sm">{stage.label}</span>
                      </div>
                      <Badge variant="secondary" className="bg-white/20 text-white text-xs">
                        {stageDeals.length}
                      </Badge>
                    </div>
                    <div className={`min-h-[150px] sm:min-h-[200px] max-h-[40vh] overflow-y-auto rounded-b-lg border-2 border-t-0 ${stage.lightColor} p-1.5 sm:p-2 space-y-1.5 sm:space-y-2`}>
                      {stageDeals.map(deal => (
                        <Card key={deal.bigin_id || deal.id} className="bg-white dark:bg-gray-800">
                          <CardContent className="p-1.5 sm:p-2">
                            <h4 className="font-medium text-xs text-gray-900 dark:text-white truncate">
                              {deal.Deal_Name}
                            </h4>
                            <p className="text-xs text-gray-500">{formatCurrency(deal.Amount)}</p>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
);
